import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { searchAirlines as searchAirlinesLocal } from '../../utils/airlineUtils';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { noAutofillProps } from '../../utils/noAutofill';
import { usePortalContainer } from './PortalContainerContext';

/**
 * Re-rank airline results to prioritize code matches over name substring matches
 */
function rankAirlineResults(results, query) {
  const q = query.trim().toLowerCase();
  return [...results].sort((a, b) => {
    const aCode = (a.code || '').toLowerCase();
    const bCode = (b.code || '').toLowerCase();
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();

    const aExactCode = aCode === q;
    const bExactCode = bCode === q;
    if (aExactCode !== bExactCode) return aExactCode ? -1 : 1;

    const aStartsCode = aCode.startsWith(q);
    const bStartsCode = bCode.startsWith(q);
    if (aStartsCode !== bStartsCode) return aStartsCode ? -1 : 1;

    const aExactName = aName === q;
    const bExactName = bName === q;
    if (aExactName !== bExactName) return aExactName ? -1 : 1;

    const aStartsName = aName.startsWith(q);
    const bStartsName = bName.startsWith(q);
    if (aStartsName !== bStartsName) return aStartsName ? -1 : 1;

    return aName.localeCompare(bName);
  });
}

async function searchAirlines(query, limit = 15) {
  if (!query || query.trim().length === 0) return [];

  const apiBase = getApiBase();
  const trimmed = query.trim();
  const isCodeLike = /^[A-Z0-9]{1,3}$/i.test(trimmed);

  // For short code-like queries, search both Urbanride and local DB in parallel
  // to ensure code matches are found even if Urbanride only returns name matches
  if (isCodeLike) {
    const [urbanrideResults, localResults] = await Promise.all([
      (async () => {
        try {
          const resp = await axios.post(
            `${apiBase}/urbanride/airline/search`,
            { input: trimmed },
            { headers: getAuthHeaders() }
          );
          const data = resp.data;
          const airlines = data?.airlines || data || [];
          return Array.isArray(airlines) ? airlines : [];
        } catch (e) {
          return [];
        }
      })(),
      (async () => {
        try {
          return (await searchAirlinesLocal(trimmed, limit)) || [];
        } catch (e) {
          return [];
        }
      })()
    ]);

    // Merge results, deduplicating by code
    const seen = new Set();
    const merged = [];
    for (const airline of [...localResults, ...urbanrideResults]) {
      const key = (airline.code || '').toUpperCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        merged.push(airline);
      }
    }

    return rankAirlineResults(merged, trimmed).slice(0, limit);
  }

  // For longer queries (airline names), try Urbanride first then fall back
  try {
    const resp = await axios.post(
      `${apiBase}/urbanride/airline/search`,
      { input: trimmed },
      { headers: getAuthHeaders() }
    );
    const data = resp.data;
    const airlines = data?.airlines || data || [];
    if (Array.isArray(airlines) && airlines.length > 0) {
      return rankAirlineResults(airlines, trimmed).slice(0, limit);
    }
  } catch (e) {
    console.warn('Urbanride airline search failed, falling back to local DB:', e.message);
  }

  // Fallback to local database search
  try {
    const localResults = await searchAirlinesLocal(query, limit);
    return localResults || [];
  } catch (e) {
    console.warn('Local airline search also failed:', e.message);
    return [];
  }
}

/**
 * AirlineAutocomplete
 * Props:
 *  - value: string (controlled display value)
 *  - onSelect: (airline: { code: string, name: string }) => void
 *  - onChangeText: (text: string) => void
 *  - placeholder?: string
 *  - className?: string
 */
const AirlineAutocomplete = ({ value = '', onSelect, onChangeText, placeholder = 'Type airline name or code', className = '' }) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [useAbsolutePositioning, setUseAbsolutePositioning] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  // Refs to prevent race conditions during selection
  const selectingRef = useRef(false);
  const justSelectedRef = useRef(false);
  const isFocusedRef = useRef(false);
  // Get portal container from context (inside Dialog) or fall back to document.body
  const portalContainer = usePortalContainer();

  // Update dropdown position when open changes
  // When inside a Radix Dialog (which uses CSS transform), we need to calculate
  // position relative to the portal container, not the viewport
  useEffect(() => {
    if (open && inputRef.current && portalContainer) {
      const inputRect = inputRef.current.getBoundingClientRect();

      // If portal container is document.body, use viewport-relative fixed positioning
      // Otherwise, calculate position relative to the portal container (absolute positioning)
      if (portalContainer === document.body) {
        setUseAbsolutePositioning(false);
        setDropdownPosition({
          top: inputRect.bottom,
          left: inputRect.left,
          width: inputRect.width
        });
      } else {
        // We're inside a custom container (likely inside a Dialog with CSS transform)
        // Calculate position relative to the container and use absolute positioning
        setUseAbsolutePositioning(true);
        const containerRect = portalContainer.getBoundingClientRect();
        setDropdownPosition({
          top: inputRect.bottom - containerRect.top,
          left: inputRect.left - containerRect.left,
          width: inputRect.width
        });
      }
    }
  }, [open, portalContainer]);

  // Keep local query in sync if parent value changes externally
  // Skip syncing when input is focused (user is actively typing) or after selection
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (isFocusedRef.current) return;
    setQuery(value || '');
  }, [value]);

  // Fetch results with debounce
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const q = (query || '').trim();
        if (!q) {
          setResults([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        const list = await searchAirlines(q, 15);
        setResults(Array.isArray(list) ? list : []);
      } catch (e) {
        console.warn('Airline search failed', e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      // Check if click was inside input container or dropdown portal
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inContainer && !inDropdown) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = useCallback((airline) => {
    if (selectingRef.current) return; // guard against duplicate firing
    selectingRef.current = true;

    const displayValue = `${airline.code} — ${airline.name}`;
    setQuery(displayValue);
    setOpen(false);
    setHighlight(-1);
    setResults([]); // clear results to avoid auto-reopen
    justSelectedRef.current = true;

    // Notify parent with airline data
    if (onSelect) {
      onSelect(airline);
    }

    // Also notify via onChangeText to keep parent in sync with display format
    if (onChangeText) {
      onChangeText(displayValue);
    }

    // Release the guard after the macrotask so blur/click won't re-fire
    setTimeout(() => { selectingRef.current = false; }, 0);
  }, [onSelect, onChangeText]);

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min((results?.length || 0) - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (open && results && results.length > 0) {
        const idx = highlight >= 0 ? highlight : 0;
        handleSelect(results[idx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        {...noAutofillProps}
        name="airline-search"
        className="w-full h-10 px-3 border border-ui-line rounded-md focus:border-ui-primary focus:ring-2 focus:ring-ui-blue-line bg-ui-surface text-ui-ink"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          const txt = e.target.value;
          setQuery(txt);
          if (onChangeText) onChangeText(txt);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          isFocusedRef.current = true;
          // Don't reopen dropdown immediately after a selection
          if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
          }
          setOpen(true);
        }}
        onBlur={() => {
          isFocusedRef.current = false;
        }}
        onKeyDown={onKeyDown}
      />

      {open && portalContainer && createPortal(
        <div
          ref={dropdownRef}
          data-autocomplete-dropdown="true"
          className={`${useAbsolutePositioning ? 'absolute' : 'fixed'} z-[9999] bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-64 overflow-auto`}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
          onMouseDown={(e) => {
            // Prevent blur when interacting with dropdown (scrolling, clicking)
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-ui-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ui-muted">No airlines found</div>
          ) : (
            results.map((airline, idx) => (
              <button
                key={`${airline.code}-${airline.name}`}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-ui-blue-line ${highlight === idx ? 'bg-ui-blue-bg' : ''}`}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  // Handle selection on mousedown to avoid Radix Dialog focus trap issues
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(airline);
                }}
              >
                <div className="font-medium text-ui-ink">{airline.code} — {airline.name}</div>
              </button>
            ))
          )}
        </div>,
        portalContainer
      )}
    </div>
  );
};

export default AirlineAutocomplete;
